import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin, Crosshair, Search } from "lucide-react";
import { toast } from "sonner";
import { COUNTRY_COORDS, COUNTRY_ALIASES, CITY_COORDS } from "@/lib/geo";

const pinIcon = L.divIcon({
  className: "afri-map-marker",
  html: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 36 48' width='32' height='42'>
    <path d='M18 1 C9 1 2 7.5 2 17 C2 28 18 47 18 47 C18 47 34 28 34 17 C34 7.5 27 1 18 1 Z'
          fill='#C05A3C' stroke='#FAF8F5' stroke-width='1.5'/>
    <circle cx='18' cy='17' r='6' fill='#FAF8F5'/></svg>`,
  iconSize: [32, 42],
  iconAnchor: [16, 42],
});

const Recenter = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom ?? map.getZoom(), { animate: true });
  }, [center, zoom, map]);
  return null;
};

const ClickHandler = ({ onPick }) => {
  useMapEvents({
    click(e) { onPick([e.latlng.lat, e.latlng.lng]); },
  });
  return null;
};

/**
 * Drop a marker anywhere on a small OpenStreetMap to set exact lat/lng for
 * a listing. Pre-seeded from country/city when no coords given. Optional
 * Nominatim search lets sellers find their address by typing.
 */
const LocationPicker = ({ value, onChange, country, city, address }) => {
  const initial = (() => {
    if (value && Number.isFinite(value.lat) && Number.isFinite(value.lng)) return [value.lat, value.lng];
    const c = CITY_COORDS[city] || COUNTRY_COORDS[country] || COUNTRY_COORDS[COUNTRY_ALIASES[country] || ""] || [20, 5];
    return c;
  })();
  const initialZoom = value ? 14 : (city && CITY_COORDS[city] ? 10 : 5);
  const [center, setCenter] = useState(initial);
  const [zoom, setZoom] = useState(initialZoom);
  const [query, setQuery] = useState(address || "");
  const [searching, setSearching] = useState(false);
  const [cooldown, setCooldown] = useState(false);

  const setPin = (latlng) => {
    setCenter(latlng);
    onChange({ lat: latlng[0], lng: latlng[1] });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported in this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setZoom(15);
        setPin([pos.coords.latitude, pos.coords.longitude]);
        toast.success("Pinned to your current location");
      },
      () => toast.error("Couldn't read your location — try the search box"),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const search = async (e) => {
    e?.preventDefault?.();
    const q = (query || "").trim();
    if (!q) return;
    setSearching(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(`${q} ${city || ""} ${country || ""}`.trim())}`;
      const r = await fetch(url, { headers: { "Accept-Language": "en" } });
      const data = await r.json();
      if (data?.[0]) {
        setZoom(15);
        setPin([parseFloat(data[0].lat), parseFloat(data[0].lon)]);
      } else {
        toast.error("Couldn't find that address");
      }
    } catch {
      toast.error("Address lookup failed");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="location-picker">
      <form onSubmit={search} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="pin-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a street, postcode or landmark…"
            className="pl-9"
          />
        </div>
        <Button type="submit" variant="outline" disabled={searching || cooldown} data-testid="pin-search-btn">
          {searching ? <Loader2 className="w-4 h-4 animate-spin"/> : "Find"}
        </Button>
        <Button type="button" variant="ghost" onClick={useMyLocation} data-testid="pin-use-location-btn" title="Use my location">
          <Crosshair className="w-4 h-4"/>
        </Button>
      </form>
      <div className="rounded-md overflow-hidden border border-black/10" style={{ height: 280 }}>
        <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onPick={setPin}/>
          {value?.lat != null && value?.lng != null && (
            <Marker
              position={[value.lat, value.lng]}
              icon={pinIcon}
              draggable
              eventHandlers={{ dragend: (e) => setPin([e.target.getLatLng().lat, e.target.getLatLng().lng]) }}
            />
          )}
          <Recenter center={center} zoom={zoom}/>
        </MapContainer>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <MapPin className="w-3 h-3"/>
          {value?.lat != null
            ? <>Pinned at <span className="font-mono">{value.lat.toFixed(4)}, {value.lng.toFixed(4)}</span></>
            : "Click the map (or search/use location) to drop your pin"}
        </span>
        {value?.lat != null && (
          <button type="button" onClick={() => onChange({ lat: null, lng: null })} className="text-[#C05A3C] hover:underline" data-testid="pin-clear-btn">Clear pin</button>
        )}
      </div>
    </div>
  );
};

export default LocationPicker;
